import { PlayerViewTab } from 'modules/players/domain/enums/player-view-tab';
import { TeamViewTab } from 'modules/teams/domain/enums/team-view-tab';
import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { ChampionshipStageTab } from 'modules/championship/domain/enums/championship-stage-tab';
import { StageGridTab } from 'modules/championship/domain/enums/stage-grid-tab';
import { StageMatchTab } from 'modules/championship/domain/enums/stage-match-tab';

/**
 * Шаблоны адресов вкладки «Этапы».
 *
 * Вынесены отдельным `as const`: `useMatch` выводит имена параметров из строкового
 * литерала, а свойство обычного объекта расширяется до `string` — и тогда
 * `match.params.stageId` перестаёт существовать для типов.
 */
const CHAMPIONSHIP_STEPS_ROUTES = {
  INDEX: '/championships/:id/steps',
  CREATE: '/championships/:id/steps/create',
  /** Этап на подвкладке «Редактирование» — корень без дополнительного сегмента. */
  STAGE: '/championships/:id/steps/:stageId',
  TEAMS: '/championships/:id/steps/:stageId/teams',
  GRID: '/championships/:id/steps/:stageId/grid',
  GRID_STANDING: '/championships/:id/steps/:stageId/grid/standing',
  GRID_MATRIX: '/championships/:id/steps/:stageId/grid/matrix',
  MATCHES: '/championships/:id/steps/:stageId/matches',
  MATCH: '/championships/:id/steps/:stageId/matches/:gameId',
  MATCH_RESULT: '/championships/:id/steps/:stageId/matches/:gameId/result',
  MATCH_MOMENTS: '/championships/:id/steps/:stageId/matches/:gameId/moments',
} as const;

export const Pages = {
  SIGN_IN: '/sign-in',
  SIGN_UP: '/sign-up',
  SELECT_TOURNAMENT: '/select-tournament',
  INDEX: '/',
  ABOUT: '/about',
  POLICY: '/policy',
  TERMS: '/terms',
  SETTINGS: {
    INDEX: '/settings',
    SOCIAL_NET: '/settings/social-net',
    SUBSCRIPTION: '/settings/subscription',
  },
  ORGANIZER: {
    CREATE: '/organizer/create',
  },
  TEMPLATE: {
    INDEX: '/template/',
    BOARDS: '/template/:templateId/boards',
    EDITOR: '/template/:templateId/boards/:boardId',
    PREVIEW: '/template/:templateId/boards/:boardId/preview',
    builders: {
      boards: (templateId: number) => `/template/${templateId}/boards`,
      editor: (templateId: number, boardId: number) => `/template/${templateId}/boards/${boardId}`,
      preview: (templateId: number, boardId: number) => `/template/${templateId}/boards/${boardId}/preview`,
    },
    CATALOG: {
      INDEX: '/template/catalog',
      CARD: '/template/catalog/:id',
      builders: {
        card: (id: number) => `/template/catalog/${id}`,
      },
    },
  },
  SUPPORT: '/support',
  CHAMPIONSHIPS: {
    INDEX: '/championships',
    CREATE: '/championships/create/common',
    EDIT: '/championships/:id',
    builders: {
      edit: (id: number, hash: string = ChampionshipEditTab.COMMON) => `/championships/${id}/${hash}`,
    },
    /**
     * Вкладка «Этапы» и всё, что под ней. `steps` здесь — это `stage` из домена,
     * сущности «шаг» не существует; см. глоссарий.
     *
     * Шаблоны нужны не только для `<Route path>`, но и для `useMatch` в табах и
     * хлебных крошках, поэтому перечислены целиком, а не собираются на месте.
     */
    STEPS: {
      ...CHAMPIONSHIP_STEPS_ROUTES,
      builders: {
        index: (championshipId: number) => `/championships/${championshipId}/steps`,
        create: (championshipId: number) => `/championships/${championshipId}/steps/create`,
        stage: (championshipId: number, stageId: number, tab?: ChampionshipStageTab) =>
          `/championships/${championshipId}/steps/${stageId}${tab ? `/${tab}` : ''}`,
        grid: (championshipId: number, stageId: number, tab: StageGridTab) =>
          `/championships/${championshipId}/steps/${stageId}/grid/${tab}`,
        match: (championshipId: number, stageId: number, gameId: number, tab: StageMatchTab = StageMatchTab.RESULT) =>
          `/championships/${championshipId}/steps/${stageId}/matches/${gameId}/${tab}`,
      },
    },
  },
  /** Старый адрес соревнований. Оставлен только для редиректа — новых ссылок на него не строим. */
  LEGACY_TOURNEYS: '/tourneys',
  GAMES: 'games',
  TEAMS: {
    INDEX: '/teams/',
    CREATE: '/teams/create',
    EDIT: '/teams/:id',
    builders: {
      edit: (id: number, hash: string = TeamViewTab.COMMON) => `/teams/${id}/${hash}`,
    },
  },
  OVERLAY_V2: {
    INDEX: 'organizer/:organizerId/device/:deviceId/overlay',
    CONTROL_PANEL: 'organizer/:organizerId/device/:deviceId/control-panel',
    builders: {
      // `options` нужны панели управления, когда она открывает окно оверлея:
      // конкретное табло, запрет на его смену извне и скрытый джойстик.
      index: (
        organizerId: string,
        deviceId: string,
        token: string,
        options?: { boardId?: number; locked?: boolean; hidePanel?: boolean },
      ) => {
        const search = new URLSearchParams({ token });
        if (options?.boardId !== undefined) search.append('boardId', String(options.boardId));
        if (options?.locked !== undefined) search.append('locked', String(options.locked));
        if (options?.hidePanel !== undefined) search.append('hidePanel', String(options.hidePanel));
        return `/organizer/${organizerId}/device/${deviceId}/overlay?${search.toString()}`;
      },
      controlPanel: (organizerId: string, deviceId: string, token: string) =>
        `/organizer/${organizerId}/device/${deviceId}/control-panel?${new URLSearchParams({ token }).toString()}`,
    },
  },
  ORGANIZERS: {
    INDEX: '/organizers/',
    VIEW: '/organizers/:id',
    builders: {
      view: (id: number) => `/organizers/${id}`,
    },
  },
  SCHEDULE: {
    INDEX: '/schedule/',
  },
  PLAYERS: {
    INDEX: '/players/',
    VIEW: '/players/:id',
    CREATE: '/players/create/',
    builders: {
      view: (id: number, hash: string = PlayerViewTab.COMMON) => `/players/${id}/${hash}`,
    },
  },
  MASTER_BOARDS: {
    INDEX: '/master-board',
    CONTROL_PANEL: '/master-board/control-panel/:id?',
    BOARD: '/master-board/board/:id?',
    builders: {
      controlPanel: (id: string) => `/master-board/control-panel${id ? `/${id}` : ''}`,
      board: (id: string) => `/master-board/board${id ? `/${id}` : ''}`,
    },
  },
  LOCATIONS: {
    INDEX: '/locations',
    CREATE: '/locations/create',
    VIEW: '/locations/:id',
    builders: {
      edit: (id: number) => `/locations/${id}`,
    },
  },
  APPLICATION_LINK: {
    INDEX: '/application-link',
    SUCCESS: '/success',
  },
  NOTIFICATIONS: {
    INDEX: '/notifications',
    CREATE: '/notifications/create',
    EDIT: '/notifications/edit/:id',
    builders: {
      edit: (id: number) => `/notifications/edit/${id}`,
    },
  },
  CHAMPIONSHIPS_TEST: '/championships-test',
  TESTS: {
    INDEX: '/test-components',
    COMPONENTS: '/test-components',
    COMPONENT: '/test-components/:componentName',
    PLAYERS: '/tests/players',
    FABRIC_EDITOR_DEMO: '/test-components/fabric-editor',
    TYPOGRAPHY: '/test-components/typography',
    builders: {
      component: (componentName: string) => `/test-components/${componentName}`,
    },
  },
  MIGRATION: {
    INDEX: '/migration',
  },
  CLIENT_TOURNAMENT: {
    VIEW_CHAMPIONSHIP: '/client-tournament/:idTournament/:idChampionship',
    builders: {
      viewChampionship: (idTournament: number, idChampionship: number) => `/client-tournament/${idTournament}/${idChampionship}`,
    },
  },
  ROLES_MANAGER: {
    INDEX: '/roles-manager',
    builders: {
      index: () => `/roles-manager`,
    },
  },
  DEVICES: {
    INDEX: '/devices',
  },
  ANALYTICS: {
    INDEX: '/analytics',
  },
  MEDIA_MANAGER: {
    INDEX: '/manager',
    TEMPLATES_LIBRARY: '/manager/library',
    EDITOR: '/manager/templates/:templateId',
    RENDER: '/manager/render',
    RENDER_STANDINGS: '/manager/render-standings',
    STAT_RENDER: '/manager/stat-render',
    builders: {
      editor: (templateId: number) => `/manager/templates/${templateId}`,
      render: (params: { tournamentId: number; championshipId: number; matchId: number; templateId: number; bgUrls?: string[] }) => {
        const search = new URLSearchParams({
          tournamentId: String(params.tournamentId),
          championshipId: String(params.championshipId),
          matchId: String(params.matchId),
          templateId: String(params.templateId),
        });
        params.bgUrls?.forEach((url, i) => search.append(`bg${i + 1}`, url));
        return `/manager/render?${search.toString()}`;
      },
      // Multi-match render (MULTIPLE_SCHEDULE / MULTIPLE_RESULTS). `matchId1..N` must be
      // contiguous from 1: the page's collectMatchIds stops scanning at the first gap.
      renderMultiple: (params: { matchIds: number[]; templateId: number; bgUrls?: string[] }) => {
        const search = new URLSearchParams({ templateId: String(params.templateId) });
        params.matchIds.forEach((id, i) => search.append(`matchId${i + 1}`, String(id)));
        params.bgUrls?.forEach((url, i) => search.append(`bg${i + 1}`, url));
        return `/manager/render?${search.toString()}`;
      },
      renderStandings: (params: { stageId: number; templateId: number; bgUrls?: string[] }) => {
        const search = new URLSearchParams({
          stageId: String(params.stageId),
          templateId: String(params.templateId),
        });
        params.bgUrls?.forEach((url, i) => search.append(`bg${i + 1}`, url));
        return `/manager/render-standings?${search.toString()}`;
      },
      // Player-statistics render. The ranking (which stat, which order, how many
      // slots) is part of the template (`player_ranking`), so a link only has to
      // name the championship and the template. The optional `orderBy` / `order`
      // / `top` override the template's ranking per render (each omitted field
      // inherits the template's value) — see applyPlayerRankingOverrides.
      statRender: (params: {
        championshipId: number;
        templateId: number;
        bgUrls?: string[];
        orderBy?: string;
        order?: 'asc' | 'desc';
        top?: number;
      }) => {
        const search = new URLSearchParams({
          championshipId: String(params.championshipId),
          templateId: String(params.templateId),
        });
        if (params.orderBy) search.append('orderBy', params.orderBy);
        if (params.order) search.append('order', params.order);
        if (params.top !== undefined) search.append('top', String(params.top));
        params.bgUrls?.forEach((url, i) => search.append(`bg${i + 1}`, url));
        return `/manager/stat-render?${search.toString()}`;
      },
    },
  },
};
